import { useSnackbar } from "notistack";
import { Alert } from "@mui/material";
import ErrorBox from "../common/ErrorBox";

/**
 * Custom hook that wraps notistack's useSnackbar to provide a simplified API
 * for showing different types of notifications.
 *
 * @returns {Object} Notification methods
 */
function useNotification() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Show an error message
   * @param {string} content - The error message to display
   */
  function setError(content) {
    enqueueSnackbar(content, {
      variant: "error",
      autoHideDuration: 8000,
    });
  }

  /**
   * Show an error with detailed error information using ErrorBox component
   * @param {Object} error - The error object to display
   */
  function setErrorBox(error) {
    enqueueSnackbar("", {
      variant: "error",
      content: (key) => (
        <Alert severity="error" icon={false} onClose={() => closeSnackbar(key)} sx={{ width: "100%" }}>
          <ErrorBox error={error} />
        </Alert>
      ),
    });
  }

  /**
   * Show a warning message
   * @param {string} content - The warning message to display
   */
  function setWarning(content) {
    enqueueSnackbar(content, {
      variant: "warning",
      autoHideDuration: 60000,
    });
  }

  /**
   * Show an info message
   * @param {string} content - The info message to display
   * @param {Object} [options]
   * @param {number} [options.autoHideDuration=8000] - Snackbar auto-hide duration in ms
   */
  function setInfo(content, options = {}) {
    enqueueSnackbar(content, {
      variant: "info",
      autoHideDuration: options.autoHideDuration ?? 8000,
    });
  }

  /**
   * Show a success message
   * @param {string} content - The success message to display
   */
  function setSuccess(content) {
    enqueueSnackbar(content, {
      variant: "success",
      autoHideDuration: 8000,
    });
  }

  /**
   * Show a custom notification with custom children
   * @param {React.ReactNode} children - The custom content to display
   */
  function setChildren(children) {
    enqueueSnackbar("", {
      autoHideDuration: 8000,
      content: (key) => <div onClick={() => closeSnackbar(key)}>{children}</div>,
    });
  }

  /**
   * Close a specific snackbar or all snackbars
   * @param {string|number} key - Optional key of the snackbar to close. If not provided, closes all.
   */
  function close(key) {
    closeSnackbar(key);
  }

  return {
    setError,
    setErrorBox,
    setWarning,
    setInfo,
    setSuccess,
    setChildren,
    close,
  };
}

export default useNotification;
